import { useEffect, useState } from "react";
import { Fingerprint, Loader2, RefreshCw, ShieldAlert, ShieldCheck, LogOut, Car } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { IdentifyLeak } from "./IdentifyLeak";

const when = (d) => d ? new Date(d).toLocaleString() : "—";

const Tile = ({ label, value, tone = "#4c9ffe", testId }) => (
  <div className="glass-tile flex-1 px-3 py-2" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wide text-[#6b7079]">{label}</div>
    <div className="font-mono text-[18px] font-semibold" style={{ color: tone }}>{value}</div>
  </div>
);

const UserRow = ({ u, onSignOut }) => {
  const [busy, setBusy] = useState(false);
  const flagged = u.flags.length > 0;
  const signOut = async () => {
    setBusy(true);
    try { await api.adminSignOutUser(u.user_id); toast.success(`${u.shop_name || u.email} was signed out everywhere`); onSignOut(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className={`glass-row grid grid-cols-[minmax(0,1.4fr)_repeat(3,minmax(0,1fr))_auto] items-center gap-3 px-3.5 py-2 text-[12px] ${flagged ? "ring-1 ring-[#e5484d]/50" : ""}`} data-testid={`access-user-${u.user_id}`}>
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 truncate font-medium">{flagged ? <ShieldAlert className="h-3.5 w-3.5 flex-none text-[#e5484d]" /> : <ShieldCheck className="h-3.5 w-3.5 flex-none text-[#3ecf8e]" />}{u.shop_name || u.email}</span>
        <span className="block truncate text-[11px] text-[#9aa0a8]">{u.email}</span>
        {flagged && <span className="mt-0.5 block text-[11px] text-[#ff6b70]" data-testid={`access-flags-${u.user_id}`}>{u.flags.join(" · ")}</span>}
      </span>
      <span className="font-mono text-[#c7cbd1]">{u.day_total} <span className="text-[#6b7079]">today</span><span className="block text-[10px] text-[#6b7079]">{u.total} all time</span></span>
      <span className="font-mono text-[#c7cbd1]">{u.day_vehicles} <span className="text-[#6b7079]">vehicles today</span><span className="block text-[10px] text-[#6b7079]">{u.vehicles} distinct all time</span></span>
      <span className="min-w-0 font-mono text-[#c7cbd1]">{u.day_ips} <span className="text-[#6b7079]">IPs today</span><span className="block truncate text-[10px] text-[#6b7079]">last {u.last_ip || "—"} · {when(u.last_at)}</span></span>
      <button data-testid={`access-signout-${u.user_id}`} disabled={busy} onClick={signOut} title="Sign this account out of every device" className="glass-btn flex items-center gap-1 px-2 py-1 text-[11px] text-[#ff6b70]">{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <LogOut className="h-3 w-3" />} Sign out</button>
    </div>
  );
};

export const AdminAccessTab = () => {
  const [r, setR] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => { setBusy(true); try { setR(await api.adminTemplateAccess()); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  useEffect(() => { load(); }, []);
  if (!r) return <div className="p-4 text-[12px] text-[#9aa0a8]" data-testid="access-loading"><Loader2 className="inline h-3.5 w-3.5 animate-spin" /> Loading template access log…</div>;
  return (
    <div className="space-y-4" data-testid="admin-access-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 text-[13px] font-semibold"><Fingerprint className="h-4 w-4 text-[#4c9ffe]" /> Template access <span className="font-normal text-[#6b7079]">— who opened which vehicle's templates, when and from where. One active session per account.</span></div>
        <button data-testid="access-refresh" onClick={load} disabled={busy} className="glass-btn ml-auto flex items-center gap-1 px-2.5 py-1 text-[11px]"><RefreshCw className={`h-3 w-3 ${busy ? "animate-spin" : ""}`} /> Refresh</button>
      </div>
      <div className="flex flex-wrap gap-2">
        <Tile label="Template loads · 24 h" value={r.day_total} testId="access-day-total" />
        <Tile label="Template loads · all time" value={r.total} tone="#c7cbd1" testId="access-total" />
        <Tile label="Accounts seen" value={r.users.length} tone="#c7cbd1" testId="access-users" />
        <Tile label="Flagged accounts" value={r.flagged} tone={r.flagged ? "#e5484d" : "#3ecf8e"} testId="access-flagged" />
      </div>
      {r.users.length === 0 ? <div className="glass-row p-6 text-center text-[12px] text-[#9aa0a8]" data-testid="access-empty">No customer has opened a template yet.</div> : (
        <div className="space-y-1.5" data-testid="access-users-list">
          <div className="grid grid-cols-[minmax(0,1.4fr)_repeat(3,minmax(0,1fr))_auto] gap-3 px-3.5 text-[10px] uppercase tracking-wide text-[#6b7079]"><span>Account</span><span>Loads</span><span>Vehicles</span><span>Where from</span><span /></div>
          {r.users.map((u) => <UserRow key={u.user_id} u={u} onSignOut={load} />)}
        </div>
      )}
      <IdentifyLeak />
      <div className="grid gap-4 lg:grid-cols-[1fr_1.6fr]">
        <div className="glass-row p-3" data-testid="access-top-vehicles">
          <div className="flex items-center gap-1.5 text-[12px] font-semibold"><Car className="h-3.5 w-3.5 text-[#f5a524]" /> Most opened vehicles</div>
          {r.top_vehicles.length === 0 ? <div className="mt-2 text-[11px] text-[#6b7079]">—</div> : r.top_vehicles.map((t, i) => (
            <div key={i} className="flex items-center gap-2 border-t border-white/5 py-1 text-[11px]"><span className="min-w-0 flex-1 truncate">{t.vehicle}</span><span className="font-mono text-[#c7cbd1]">{t.n}</span><span className="w-14 text-right font-mono text-[#6b7079]">{t.shops} shop{t.shops === 1 ? "" : "s"}</span></div>
          ))}
        </div>
        <div className="glass-row p-3" data-testid="access-recent">
          <div className="text-[12px] font-semibold">Recent template loads</div>
          <div className="scroll-visible mt-1 max-h-72 overflow-y-auto">
            {r.recent.map((e, i) => (
              <div key={i} className="flex items-center gap-3 border-t border-white/5 py-1 text-[11px]" data-testid={`access-event-${i}`}>
                <span className="w-36 flex-none font-mono text-[#9aa0a8]">{when(e.at)}</span>
                <span className="min-w-0 flex-1 truncate"><b className="text-[#e8e9ec]">{e.shop_name || e.email}</b> · {e.vehicle} <span className="text-[#6b7079]">({e.patterns})</span></span>
                <span className="flex-none font-mono text-[#6b7079]">{e.ip || "—"}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
